import { Loader2 } from "lucide-react";

export function Money({ minor, className = "" }) {
  const eur = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });
  return <span className={`font-mono2 tabular-nums ${className}`}>{minor == null ? "—" : eur.format(minor / 100)}</span>;
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-slate-400" data-testid="spinner">
      <Loader2 className="animate-spin" size={20} /> {label || "Caricamento..."}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#0F2042] tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1 text-sm sm:text-base">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export const LIVE = {
  disponibile: ["Disponibile", "bg-emerald-50 text-emerald-700 border-emerald-200", "#059669"],
  fuori: ["Fuori", "bg-red-50 text-red-600 border-red-200", "#DC2626"],
  rientro: ["In rientro", "bg-orange-50 text-orange-700 border-orange-200", "#EA580C"],
  ritardo: ["In ritardo", "bg-red-900 text-white border-red-900", "#7F1D1D"],
  non_disponibile: ["Non disponibile", "bg-slate-100 text-slate-600 border-slate-300", "#64748B"],
};
const SIGN = {
  draft: ["Bozza", "bg-slate-100 text-slate-600 border-slate-200"],
  awaiting_signature: ["In attesa di firma", "bg-amber-50 text-amber-700 border-amber-200"],
  signed: ["Firmato", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  cancelled: ["Annullato", "bg-rose-50 text-rose-700 border-rose-200"],
};
const RENTAL = {
  scheduled: ["Da consegnare", "bg-sky-50 text-sky-700 border-sky-200"],
  out: ["Fuori", "bg-red-50 text-red-600 border-red-200"],
  returned: ["Rientrato", "bg-slate-100 text-slate-600 border-slate-200"],
  cancelled: ["Annullato", "bg-rose-50 text-rose-700 border-rose-200"],
};
export const FINE = {
  ricevuta: ["Ricevuta", "bg-sky-50 text-sky-700 border-sky-200"],
  da_identificare: ["Da identificare", "bg-amber-50 text-amber-700 border-amber-200"],
  cliente_identificato: ["Cliente identificato", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  in_verifica: ["In verifica", "bg-orange-50 text-orange-700 border-orange-200"],
  dati_comunicati: ["Dati comunicati", "bg-indigo-50 text-indigo-700 border-indigo-200"],
  inviata_cliente: ["Inviata al cliente", "bg-violet-50 text-violet-700 border-violet-200"],
  pagata: ["Pagata", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  contestata: ["Contestata", "bg-rose-50 text-rose-700 border-rose-200"],
  chiusa: ["Chiusa", "bg-slate-100 text-slate-500 border-slate-200"],
};
const OVERRIDE = { maintenance: "Manutenzione", damaged: "Danneggiato", blocked: "Bloccato" };
export const overrideLabel = (s) => OVERRIDE[s] || "";

function Pill({ map, value, testid }) {
  const [label, cls] = map[value] || [value || "—", "bg-slate-100 text-slate-600 border-slate-200"];
  return (
    <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {label}
    </span>
  );
}

export const LiveBadge = ({ status, testid }) => <Pill map={LIVE} value={status} testid={testid} />;
export const SignBadge = ({ status, testid }) => <Pill map={SIGN} value={status} testid={testid} />;
export const RentalBadge = ({ status, testid }) => <Pill map={RENTAL} value={status} testid={testid} />;
export const FineBadge = ({ status, testid }) => <Pill map={FINE} value={status} testid={testid} />;

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="empty-state">
      {Icon && <Icon className="text-slate-300 mb-3" size={38} />}
      <p className="text-slate-600 font-medium">{title}</p>
      {hint && <p className="text-slate-400 text-sm mt-1">{hint}</p>}
    </div>
  );
}

export function Card({ title, actions, children, className = "", testid }) {
  return (
    <section className={`bg-white border border-slate-200 rounded-xl ${className}`} data-testid={testid}>
      {title && (
        <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#0F2042] text-sm uppercase tracking-wide">{title}</h2>
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function KV({ label, value, testid }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</p>
      <p className="text-sm text-[#0F2042] font-medium break-words" data-testid={testid}>{value || value === 0 ? value : "—"}</p>
    </div>
  );
}

export function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-[#C25E00]"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export const btnPrimary = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0F2042] text-white text-sm font-semibold hover:bg-[#162C5A] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50";
export const btnAccent = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#C25E00] text-white text-sm font-semibold hover:bg-[#A84F00] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50";
export const btnGhost = "inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-[#0F2042] text-sm font-semibold hover:border-[#C25E00] hover:text-[#C25E00] transition-colors disabled:opacity-50";
export const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2042]/20 focus:border-[#0F2042]";
